import { isProjectTypeProjectBankFolder } from './projectBankForms';

/**
 * Kinds of the sidebar's category rows: the fixed ("hardcoded") folders of
 * the project root that the bank creates itself, plus the tilaajavastuu
 * documents view. Everything else under the root (user-created folders,
 * sub-projects) is browsed through the folder tree.
 */
export const BANK_CATEGORY_KINDS = {
  IMAGES: 'images',
  DOCUMENTS: 'documents',
  FORMS: 'forms',
  REPORTS: 'reports',
  TILAAJAVASTUU: 'tilaajavastuu',
  RECORDINGS: 'recordings',
  /** The cameras' clips and stills ("Kameratallenteet"). */
  CAMERA_RECORDINGS: 'cameraRecordings',
  MEETING_NOTES: 'meetingNotes',
  EMAIL: 'email',
  INVOICE_IMAGES: 'invoiceImages',
  /** A system folder known only by its translation key. */
  SYSTEM: 'system',
};

/** Row order of the categories in the sidebar. */
export const BANK_CATEGORY_ORDER = [
  BANK_CATEGORY_KINDS.IMAGES,
  BANK_CATEGORY_KINDS.DOCUMENTS,
  BANK_CATEGORY_KINDS.FORMS,
  BANK_CATEGORY_KINDS.REPORTS,
  BANK_CATEGORY_KINDS.TILAAJAVASTUU,
  BANK_CATEGORY_KINDS.RECORDINGS,
  BANK_CATEGORY_KINDS.CAMERA_RECORDINGS,
  BANK_CATEGORY_KINDS.MEETING_NOTES,
  BANK_CATEGORY_KINDS.EMAIL,
  BANK_CATEGORY_KINDS.INVOICE_IMAGES,
  BANK_CATEGORY_KINDS.SYSTEM,
];

// The raw names of the system folders (the keys of getSystemFolderNames).
const KIND_BY_FOLDER_NAME = {
  images: BANK_CATEGORY_KINDS.IMAGES,
  documents: BANK_CATEGORY_KINDS.DOCUMENTS,
  forms: BANK_CATEGORY_KINDS.FORMS,
  reports: BANK_CATEGORY_KINDS.REPORTS,
  'videochat-recordings': BANK_CATEGORY_KINDS.RECORDINGS,
};

// System folders the backend labels through bank.translated_items.*.
const KIND_BY_TRANSLATION_KEY = {
  videochat_recordings_folder: BANK_CATEGORY_KINDS.RECORDINGS,
  // The camera gateway's motion clips and stills (camera-feeds plan, 13.2):
  // `camera-recordings-<projectId>`, a folder per camera under it and in
  // each a "Kuvat" folder (camera_stills_folder) of day folders.
  camera_recordings_folder: BANK_CATEGORY_KINDS.CAMERA_RECORDINGS,
  videochat_notes_folder: BANK_CATEGORY_KINDS.MEETING_NOTES,
  project_email_root_folder: BANK_CATEGORY_KINDS.EMAIL,
  invoice_images_folder: BANK_CATEGORY_KINDS.INVOICE_IMAGES,
};

/** Id prefix of the (client-side only) tilaajavastuu documents folder. */
export const TILAAJAVASTUU_FOLDER_ID_PREFIX = 'tilaajavastuu-folder_';

/**
 * Id of the tilaajavastuu documents pseudo-folder of a project (the id the
 * tree row and the folder tile select).
 * @param {string} projectId
 * @returns {string}
 */
export const getTilaajavastuuFolderId = (projectId) =>
  `${TILAAJAVASTUU_FOLDER_ID_PREFIX}${projectId}`;

/**
 * Project id of a folder whose `project` is an id, a joined object, or that
 * carries a plain `projectId`.
 * @param {Object} folder
 * @returns {string|null}
 */
export const getFolderProjectId = (folder) => {
  if (!folder) return null;
  if (folder.project && typeof folder.project === 'object') {
    return folder.project.id ?? null;
  }
  return folder.project ?? folder.projectId ?? null;
};

/**
 * Category kind of a folder that sits directly under the project root: a
 * system folder by name (images, documents, forms, reports, videochat
 * recordings), a system folder by translation key (project email, invoice
 * images, ...), or null for a user-created folder and a sub-project folder.
 * @param {Object} folder raw public folder
 * @returns {string|null} one of BANK_CATEGORY_KINDS
 */
export function getBankCategoryKind(folder) {
  if (!folder || folder.type !== 'FOLDER') return null;
  if (isProjectTypeProjectBankFolder(folder)) return null;
  if (folder.translationKey) {
    return (
      KIND_BY_TRANSLATION_KEY[folder.translationKey] ||
      BANK_CATEGORY_KINDS.SYSTEM
    );
  }
  return KIND_BY_FOLDER_NAME[folder.name] || null;
}

const rankOf = (kind) => {
  const index = BANK_CATEGORY_ORDER.indexOf(kind);
  return index === -1 ? BANK_CATEGORY_ORDER.length : index;
};

const createdMs = (folder) => {
  const ms = folder && folder.createdAt ? Date.parse(folder.createdAt) : NaN;
  return Number.isNaN(ms) ? Number.POSITIVE_INFINITY : ms;
};

// The earlier created of two duplicate system folders; ties by id so the
// pick never depends on entity order.
const isCreatedBefore = (folder, other) => {
  const a = createdMs(folder);
  const b = createdMs(other);
  if (a !== b) return a < b;
  return String(folder.id) < String(other.id);
};

/**
 * @typedef {Object} BankSidebarCategory
 * @property {string} id folder id (the tilaajavastuu pseudo-folder id for that kind)
 * @property {string} kind one of BANK_CATEGORY_KINDS
 * @property {Object|null} raw the raw folder; null for the tilaajavastuu view
 * @property {string|null} projectId project of the root
 * @property {boolean} droppable the row may receive items dragged from the folder view
 */

/**
 * The category rows of a project root: its system folders (one row per kind;
 * a project can hold duplicate system folders, the earliest created stands
 * for the kind) and, when asked, the tilaajavastuu documents view, in
 * BANK_CATEGORY_ORDER.
 * @param {Object} params
 * @param {Object} [params.root] root folder of the tree (the open project's folder)
 * @param {Array<Object>} [params.folders=[]] every public folder (raw)
 * @param {boolean} [params.includeTilaajavastuu=false] add the tilaajavastuu row
 * @returns {Array<BankSidebarCategory>}
 */
export function buildBankCategories({
  root,
  folders = [],
  includeTilaajavastuu = false,
}) {
  if (!root) return [];
  const projectId = getFolderProjectId(root);
  const byKind = new Map();
  const systemFolders = [];

  folders.forEach((folder) => {
    if (!folder || folder.parent !== root.id) return;
    const kind = getBankCategoryKind(folder);
    if (!kind) return;
    const category = {
      id: folder.id,
      kind,
      raw: folder,
      projectId,
      droppable:
        folder.isVirtualProjectBankForm !== true && folder.isReadOnly !== true,
    };
    if (kind === BANK_CATEGORY_KINDS.SYSTEM) {
      systemFolders.push(category);
      return;
    }
    const existing = byKind.get(kind);
    if (!existing || isCreatedBefore(folder, existing.raw)) {
      byKind.set(kind, category);
    }
  });

  if (includeTilaajavastuu && projectId) {
    byKind.set(BANK_CATEGORY_KINDS.TILAAJAVASTUU, {
      id: getTilaajavastuuFolderId(projectId),
      kind: BANK_CATEGORY_KINDS.TILAAJAVASTUU,
      raw: null,
      projectId,
      droppable: false,
    });
  }

  return [...byKind.values(), ...systemFolders].sort(
    (a, b) =>
      rankOf(a.kind) - rankOf(b.kind) ||
      String(a.raw ? a.raw.name : '').localeCompare(
        String(b.raw ? b.raw.name : '')
      )
  );
}

/**
 * The category the open folder belongs to: the selected folder itself or
 * any ancestor of it that is a category row of the root, or the
 * tilaajavastuu row while the root project's tilaajavastuu view is open.
 * Null when the open folder is the root, a user-created folder, a
 * sub-project (or anything inside one): those are browsed through the tree.
 * @param {Object} params
 * @param {Array<BankSidebarCategory>} params.categories rows of buildBankCategories
 * @param {Object} [params.root] root folder of the tree
 * @param {string} [params.selectedFolderId] id of the open public folder
 * @param {Object} [params.foldersById={}] every public folder by id
 * @param {string} [params.tilaajavastuuProjectId] project whose tilaajavastuu view is open
 * @returns {string|null} category id
 */
export function findActiveBankCategoryId({
  categories,
  root,
  selectedFolderId,
  foldersById = {},
  tilaajavastuuProjectId,
}) {
  if (!root || !categories || categories.length === 0) return null;

  if (tilaajavastuuProjectId) {
    const category = categories.find(
      (candidate) => candidate.kind === BANK_CATEGORY_KINDS.TILAAJAVASTUU
    );
    return category &&
      String(tilaajavastuuProjectId) === String(getFolderProjectId(root))
      ? category.id
      : null;
  }

  // Climb to the folder directly under the root (a cycle guard in case the
  // parent links are ever inconsistent).
  let folder = selectedFolderId ? foldersById[selectedFolderId] : null;
  const seen = new Set();
  while (
    folder &&
    folder.parent &&
    folder.parent !== root.id &&
    !seen.has(folder.id)
  ) {
    seen.add(folder.id);
    folder = foldersById[folder.parent];
  }
  if (!folder || folder.parent !== root.id) return null;

  const direct = categories.find((candidate) => candidate.id === folder.id);
  if (direct) return direct.id;

  // A duplicate system folder that another folder stands for in the rows.
  const kind = getBankCategoryKind(folder);
  if (!kind || kind === BANK_CATEGORY_KINDS.SYSTEM) return null;
  const byKind = categories.find((candidate) => candidate.kind === kind);
  return byKind ? byKind.id : null;
}
